/**
 * 员工会话（spec-identity AA-04 `GET /admin-api/v1/auth/session`）与菜单过滤（PRD §3.3.1"员工只看到自己岗位有权限的菜单"；
 * SPEC-INDEX §5：菜单按 `Ann.view` 权限码过滤）。会话每 60 秒以 passive 方式轮询（不刷新无操作计时），ws `admin.session`
 * 推送时立即重新取（AdminLayout）；权限版本变化时当前页面变为无权才提示"权限已变更"并回到工作台（spec-platform F 节"后台通用"）。
 * 审计只读模式的当前状态：AA-04 带回 `readonlyMode` 时以它为准；没有这个字段时由 AdminLayout 在只读期间探测 A01 顶栏接口。
 */
import { useQuery } from '@tanstack/react-query';
import { ADMIN_PAGES, MENU_GROUPS, menuPages, type AdminPageId, type AdminPageRoute, type MenuGroup } from '../../routes';
import { adminApi } from './api/client';

/** AA-04 响应中外壳用到的字段（StaffSelf 其余字段由员工领域使用）。 */
export interface StaffSession {
  staff: { staffNo: string; name: string; roles: readonly string[]; passwordRotateDue?: boolean };
  perms: readonly string[];
  permVersion: number | string;
  menu?: readonly string[];
  locked: boolean;
  expiresAt?: string;
  idleLockAt?: string;
  /** 审计异常只读模式（核心 H9）；AA-04 未提供时为 undefined */
  readonlyMode?: boolean;
}

export const SESSION_QUERY_KEY = ['auth', 'session'] as const;
export const SESSION_POLL_MS = 60_000;
/** 只读期间探测审计是否恢复的间隔 */
export const READONLY_PROBE_MS = 30_000;
export const READONLY_PROBE_KEY = ['admin-shell', 'readonly-probe'] as const;

/**
 * 只读期间的探测：A01 顶栏接口（GET `/workbench/header`，A01.view，全部岗位都有）返回 `readonlyMode`（spec-platform D.1 #1）。
 * passive，不刷新无操作计时；GET 不受只读模式影响。
 */
export function fetchReadonlyMode(): Promise<boolean> {
  return adminApi.get<{ readonlyMode?: boolean }>('/workbench/header', { passive: true }).then((header) => header.readonlyMode === true);
}

export function fetchStaffSession(): Promise<StaffSession> {
  return adminApi.get<StaffSession>('/auth/session', { passive: true });
}

export function useStaffSession() {
  return useQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchStaffSession, refetchInterval: SESSION_POLL_MS, staleTime: SESSION_POLL_MS / 2 });
}

/** 页面查看权限码 `Ann.view`（spec-core §5.3）。 */
export function viewPerm(pageId: AdminPageId): string {
  return `${pageId}.view`;
}

export function canView(perms: Iterable<string>, pageId: AdminPageId): boolean {
  const set = perms instanceof Set ? (perms as Set<string>) : new Set(perms);
  return set.has(viewPerm(pageId));
}

export interface VisibleMenuGroup {
  group: MenuGroup;
  pages: AdminPageRoute[];
}

/** 按权限过滤后的菜单：只保留有 Ann.view 的页面，没有可见页面的菜单组整组不显示。 */
export function visibleMenu(perms: Iterable<string>): VisibleMenuGroup[] {
  const set = new Set(perms);
  return MENU_GROUPS.map((group) => ({ group, pages: menuPages(group).filter((page) => set.has(viewPerm(page.id))) })).filter(
    (entry) => entry.pages.length > 0,
  );
}

/** 本人有权限的全部页面（含不在菜单中的详情页），供快捷入口等使用。 */
export function viewablePages(perms: Iterable<string>): AdminPageRoute[] {
  const set = new Set(perms);
  return ADMIN_PAGES.filter((page) => set.has(viewPerm(page.id)));
}
